import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/nurseryApi";
import { ArrowRight, Leaf, CheckCircle, Star } from "@phosphor-icons/react";

const blank = {
  nursery_name: "", owner_name: "", email: "", phone: "", address: "", website: "",
  crew_size: "2-5", years_in_business: "", top_plants: "", current_method: "", why_now: "", referral: "",
};

export default function Apply() {
  const [form, setForm] = useState(blank);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/applications", form);
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Application failed. Try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="organic-bg" style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
        <div className="card-soft" style={{ width: "100%", maxWidth: 580, padding: 44, borderRadius: 24, textAlign: "center" }}>
          <div style={{ width: 72, height: 72, borderRadius: 24, background: "var(--primary-soft)", display: "grid", placeItems: "center", margin: "0 auto" }}>
            <CheckCircle size={36} weight="fill" color="var(--primary)" />
          </div>
          <h1 className="font-display" style={{ fontSize: 40, marginTop: 18, lineHeight: 1.05 }}>Application received.</h1>
          <p style={{ fontSize: 16, color: "var(--text-soft)", marginTop: 14, lineHeight: 1.6 }}>
            We hand-pick 5 <b>Founding Nurseries</b> for Spring 2026. We'll personally review yours within 48 hours and call you to set things up. Keep your phone handy.
          </p>
          <div style={{ marginTop: 24, display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/" className="btn btn-outline">Back to home</Link>
            <Link to="/blog" className="btn btn-primary">Read Field Notes <ArrowRight size={14} weight="bold" /></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="organic-bg" style={{ minHeight: "100vh", padding: "32px 16px 80px" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <Link to="/" style={{ fontSize: 12, fontWeight: 700, color: "var(--secondary)", letterSpacing: "0.1em", textTransform: "uppercase", textDecoration: "none" }} data-testid="apply-back-home">← Back home</Link>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 18 }}>
          <div style={{ width: 48, height: 48, background: "var(--secondary)", borderRadius: 14, display: "grid", placeItems: "center" }}>
            <Star size={24} color="white" weight="fill" />
          </div>
          <span className="badge badge-soft">Spring 2026 · 5 slots · hand-picked</span>
        </div>
        <h1 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 64px)", marginTop: 18, lineHeight: 1.02, letterSpacing: "-0.02em" }}>
          Apply to be a <span className="font-script" style={{ color: "var(--secondary)" }}>Founding Nursery</span>.
        </h1>
        <p style={{ fontSize: 17, color: "var(--text-soft)", marginTop: 16, lineHeight: 1.6, maxWidth: 580 }}>
          We're choosing five Warren County nurseries to run a 2-week field test of NurserySense this spring. Founding Nurseries get <b>lifetime-locked pricing</b>, their name on our launch page, first say on new features, and white-glove setup at their gate. Tell us a little about you.
        </p>

        <form onSubmit={submit} className="card-soft" style={{ marginTop: 28, padding: 32, borderRadius: 22, display: "flex", flexDirection: "column", gap: 18 }}>
          <Section title="The nursery">
            <Row>
              <Field label="Nursery name *" v={form.nursery_name} on={set("nursery_name")} req testid="apply-nursery" />
              <Field label="Your name *" v={form.owner_name} on={set("owner_name")} req testid="apply-owner" />
            </Row>
            <Row>
              <Field label="Email *" type="email" v={form.email} on={set("email")} req testid="apply-email" />
              <Field label="Phone *" v={form.phone} on={set("phone")} req placeholder="(931) 555-0182" testid="apply-phone" />
            </Row>
            <Field label="Address" v={form.address} on={set("address")} placeholder="1428 Manchester Hwy, McMinnville, TN 37110" testid="apply-address" />
            <Field label="Website (if any)" v={form.website} on={set("website")} placeholder="https://your-nursery.com" testid="apply-website" />
          </Section>

          <Section title="A little about your operation">
            <Row>
              <div>
                <label className="label">Crew size *</label>
                <select className="select" value={form.crew_size} onChange={set("crew_size")} required data-testid="apply-crew">
                  <option value="solo">Just me</option>
                  <option value="2-5">2 – 5</option>
                  <option value="6-15">6 – 15</option>
                  <option value="16+">16 or more</option>
                </select>
              </div>
              <Field label="Years in business" v={form.years_in_business} on={set("years_in_business")} placeholder="e.g. 12" testid="apply-years" />
            </Row>
            <Field label="Top plants you grow" v={form.top_plants} on={set("top_plants")} placeholder="e.g. Eastern Redbud, Boxwood, Crepe Myrtle" testid="apply-plants" />
            <Area label="How do you handle scouting & inventory today?" v={form.current_method} on={set("current_method")} rows={3} placeholder="Paper notebook? Spreadsheet? Just memory? Be honest — that helps us help you." testid="apply-method" />
          </Section>

          <Section title="The why">
            <Area label="Why now? What's the one thing you'd want NurserySense to fix this spring? *" v={form.why_now} on={set("why_now")} rows={4} req testid="apply-why" placeholder="The more specific, the better — this is how we choose." />
            <Field label="Who referred you? (optional)" v={form.referral} on={set("referral")} placeholder="TNLA, a neighbor, our blog…" testid="apply-referral" />
          </Section>

          {err && <div className="badge badge-danger" data-testid="apply-error">{err}</div>}

          <button className="btn btn-primary" type="submit" disabled={loading} data-testid="apply-submit-btn" style={{ padding: "16px 28px", fontSize: 16 }}>
            {loading ? "Sending…" : <>Submit my application <ArrowRight size={18} weight="bold" /></>}
          </button>
          <div style={{ fontSize: 12.5, color: "var(--muted-fg)", textAlign: "center" }}>
            Already a Founding Nursery? <Link to="/login" style={{ fontWeight: 700, color: "var(--primary)", textDecoration: "underline" }} data-testid="apply-goto-login">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

const Row = ({ children }) => <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }} className="row-2">{children}<style>{`@media(max-width:640px){.row-2{grid-template-columns:1fr !important;}}`}</style></div>;
const Section = ({ title, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--secondary)" }}>{title}</div>
    {children}
  </div>
);
const Field = ({ label, v, on, req, type = "text", placeholder, testid }) => (
  <div>
    <label className="label">{label}</label>
    <input className="input" type={type} value={v} onChange={on} required={req} placeholder={placeholder} data-testid={testid} />
  </div>
);
const Area = ({ label, v, on, req, rows = 3, placeholder, testid }) => (
  <div>
    <label className="label">{label}</label>
    <textarea className="textarea" rows={rows} value={v} onChange={on} required={req} placeholder={placeholder} data-testid={testid} />
  </div>
);
